import { CompanyPermissionTypes, IndustryT, SuggestedActionLink } from '@/lib/types/CompanyT'
import { MapperType } from '@/lib/types/utilsT'

export type CompanyPermissionType = MapperType<CompanyPermissionTypes>

export const COMPANY_PERMISSIONS = {
  GET_DASHBOARD: 'get_dashboard',
  USE_CONTENT: 'use_content',
  USE_GIFT_COUPONS: 'use_gift_coupons',
  SETUP_PAID_PROGRAMS: 'setup_paid_programs',
  SETUP_PROGRAM_CONTACT_MESSAGE: 'setup_program_contact_message',
  USE_MESSAGING: 'use_messaging',
} satisfies CompanyPermissionType

export type IndustryType = MapperType<IndustryT>

export const INDUSTRIES = {
  RESTAURANT: 'restaurant',
  PIZZA_SHOP: 'pizza_shop',
  FRANCHISE_RESTAURANT: 'franchise_restaurant',
  DOCTORS_OFFICE: 'doctors_office',
  DENTIST_OFFICE: 'dentist_office',
  VETERINARIAN: 'veterinarian',
  SCHOOL: 'school',
  PRE_SCHOOL: 'pre-school',
  K_8: 'k_8',
  HIGH_SCHOOL: 'high_school',
  COLLEGE: 'college',
  UNIVERSITY: 'university',
  SALON: 'salon',
  SPA: 'spa',
  WELLNESS_SERVICE: 'wellness_service',
  CHARITY: 'charity',
  NONPROFIT: 'nonprofit',
  HVAC: 'hvac',
  PLUMBER: 'plumber',
  HOME_SERVICE: 'home_service',
  HOUSE_CLEANER: 'house_cleaner',
  AUTO_MAINTENANCE: 'auto_maintenance',
  DOG_WALKER: 'dog_walker',
  PET_GROOMER: 'pet_groomer',
  PET_BOARDING: 'pet_boarding',
  OTHER: 'other',
} satisfies IndustryType

export type Industry = (typeof INDUSTRIES)[keyof typeof INDUSTRIES]
type IndustryConfigItem = {
  label: string
  emoji: string
  taxonomy: string[]
}

export const INDUSTRY_CONFIG = {
  [INDUSTRIES.RESTAURANT]: {
    label: 'Restaurant',
    emoji: '🍴',
    taxonomy: ['Food', 'Dining', 'Local eatery', 'Reservations', 'Menu'],
  },
  [INDUSTRIES.PIZZA_SHOP]: {
    label: 'Pizza shop',
    emoji: '🍕',
    taxonomy: ['Food', 'Pizza', 'Delivery', 'Takeout', 'Local restaurant'],
  },
  [INDUSTRIES.FRANCHISE_RESTAURANT]: {
    label: 'Franchise restaurant',
    emoji: '🏪',
    taxonomy: ['Food', 'Chain', 'Dining', 'Franchise', 'Multi-location'],
  },
  [INDUSTRIES.DOCTORS_OFFICE]: {
    label: "Doctor's office",
    emoji: '🏥',
    taxonomy: ['Health', 'Medical', 'Primary care', 'Physician', 'Appointment'],
  },
  [INDUSTRIES.DENTIST_OFFICE]: {
    label: 'Dentist office',
    emoji: '🦷',
    taxonomy: ['Health', 'Dental', 'Teeth', 'Oral care', 'Doctor'],
  },
  [INDUSTRIES.VETERINARIAN]: {
    label: 'Veterinarian',
    emoji: '🐾',
    taxonomy: ['Pet', 'Animal health', 'Vet', 'Medical', 'Pet care'],
  },
  [INDUSTRIES.SCHOOL]: {
    label: 'School',
    emoji: '🏫',
    taxonomy: ['Education', 'K-12', 'Students', 'Learning', 'Campus'],
  },
  [INDUSTRIES.PRE_SCHOOL]: {
    label: 'Pre-school',
    emoji: '🎒',
    taxonomy: ['Education', 'K-12', 'Students', 'Learning', 'Campus'],
  },
  [INDUSTRIES.K_8]: {
    label: 'K-8',
    emoji: '📚',
    taxonomy: ['Education', 'K-12', 'Students', 'Learning', 'Campus'],
  },
  [INDUSTRIES.HIGH_SCHOOL]: {
    label: 'High school',
    emoji: '🎓',
    taxonomy: ['Education', 'K-12', 'Students', 'Learning', 'Campus'],
  },
  [INDUSTRIES.COLLEGE]: {
    label: 'College',
    emoji: '🎓',
    taxonomy: ['Education', 'Higher ed', 'Campus', 'Admissions', 'Academic', 'Alumni'],
  },
  [INDUSTRIES.UNIVERSITY]: {
    label: 'University',
    emoji: '🎓',
    taxonomy: ['Education', 'Higher ed', 'Campus', 'Admissions', 'Academic', 'Alumni'],
  },
  [INDUSTRIES.SALON]: {
    label: 'Salon',
    emoji: '✂️',
    taxonomy: ['Beauty', 'Wellness', 'Appointment', 'Self-care', 'Service'],
  },
  [INDUSTRIES.SPA]: {
    label: 'Spa',
    emoji: '💆',
    taxonomy: ['Beauty', 'Wellness', 'Appointment', 'Self-care', 'Service'],
  },
  [INDUSTRIES.WELLNESS_SERVICE]: {
    label: 'Wellness service',
    emoji: '🧘',
    taxonomy: ['Beauty', 'Wellness', 'Appointment', 'Self-care', 'Service'],
  },
  [INDUSTRIES.CHARITY]: {
    label: 'Charity',
    emoji: '❤️',
    taxonomy: ['Nonprofit', 'Donation', 'Community', 'Mission', 'Volunteer'],
  },
  [INDUSTRIES.NONPROFIT]: {
    label: 'Nonprofit',
    emoji: '🌱',
    taxonomy: ['Nonprofit', 'Donation', 'Community', 'Mission', 'Volunteer'],
  },
  [INDUSTRIES.HVAC]: {
    label: 'HVAC',
    emoji: '🌡️',
    taxonomy: ['Home services', 'Repair', 'Trades', 'Emergency service', 'Contractor'],
  },
  [INDUSTRIES.PLUMBER]: {
    label: 'Plumber',
    emoji: '🔧',
    taxonomy: ['Home services', 'Repair', 'Trades', 'Emergency service', 'Contractor'],
  },
  [INDUSTRIES.HOME_SERVICE]: {
    label: 'Home service',
    emoji: '🏠',
    taxonomy: ['Home services', 'Repair', 'Trades', 'Emergency service', 'Contractor'],
  },
  [INDUSTRIES.HOUSE_CLEANER]: {
    label: 'House cleaner',
    emoji: '🧹',
    taxonomy: ['Home services', 'Cleaning', 'Residential', 'Recurring service', 'Booking'],
  },
  [INDUSTRIES.AUTO_MAINTENANCE]: {
    label: 'Auto maintenance',
    emoji: '🚗',
    taxonomy: ['Car', 'Repair', 'Vehicle', 'Service provider', 'Mechanic'],
  },
  [INDUSTRIES.DOG_WALKER]: {
    label: 'Dog walker',
    emoji: '🐕',
    taxonomy: ['Pet', 'Dog', 'Walking', 'Recurring service', 'Outdoor'],
  },
  [INDUSTRIES.PET_GROOMER]: {
    label: 'Pet groomer',
    emoji: '🐩',
    taxonomy: ['Pet', 'Grooming', 'Boarding', 'Animal care', 'Appointment'],
  },
  [INDUSTRIES.PET_BOARDING]: {
    label: 'Pet boarding',
    emoji: '🏡',
    taxonomy: ['Pet', 'Grooming', 'Boarding', 'Animal care', 'Appointment'],
  },
  [INDUSTRIES.OTHER]: {
    label: 'General',
    emoji: '🔗',
    taxonomy: [],
  },
} satisfies Record<Industry, IndustryConfigItem>

export type IndustryDropdownItem = {
  value: Industry
  title: string
}
export const INDUSTRY_DROPDOWN = Object.entries(INDUSTRY_CONFIG).map(([key, value]) => ({
  value: key as Industry,
  title: `${value.emoji} ${value.label}`,
}))

export const INDUSTRY_SUGGESTED_ACTION_LINKS = {
  restaurant: [
    {
      clientType: 'order_delivery_takeout',
      label: 'Order delivery / takeout',
      url: '',
      icon: 'box',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'reserve_table',
      label: 'Reserve a table',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-violet-600 dark:fill-violet-400',
      classIconBg: 'bg-violet-100 dark:bg-violet-900/30',
    },
    {
      clientType: 'view_menu',
      label: 'View the menu',
      url: '',
      icon: 'document',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'hours_directions',
      label: 'Hours & directions',
      url: '',
      icon: 'marker',
      classIcon: 'fill-red-500 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
    {
      clientType: 'private_events_catering',
      label: 'Private events / catering',
      url: '',
      icon: 'star',
      classIcon: 'fill-purple-600 dark:fill-purple-400',
      classIconBg: 'bg-purple-100 dark:bg-purple-900/30',
    },
  ],

  pizza_shop: [
    {
      clientType: 'order_online',
      label: 'Order online',
      url: '',
      icon: 'link',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'view_menu_deals',
      label: 'View menu / deals',
      url: '',
      icon: 'document',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'track_order',
      label: 'Track order',
      url: '',
      icon: 'box',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'pickup_location',
      label: 'Hours & pickup location',
      url: '',
      icon: 'marker',
      classIcon: 'fill-red-500 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
  ],

  franchise_restaurant: [
    {
      clientType: 'find_location',
      label: 'Find a location',
      url: '',
      icon: 'marker',
      classIcon: 'fill-red-500 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
    {
      clientType: 'order_from_location',
      label: 'Order from this location',
      url: '',
      icon: 'link',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'view_menu',
      label: 'View menu',
      url: '',
      icon: 'document',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'rewards_signup',
      label: 'Rewards / app sign-up',
      url: '',
      icon: 'crown',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    {
      clientType: 'gift_cards',
      label: 'Gift cards',
      url: '',
      icon: 'card',
      classIcon: 'fill-purple-500 dark:fill-purple-400',
      classIconBg: 'bg-purple-100 dark:bg-purple-900/30',
    },
  ],

  doctors_office: [
    {
      clientType: 'book_appointment',
      label: 'Book appointment',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'patient_portal',
      label: 'Patient portal login',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'patient_forms',
      label: 'Patient forms',
      url: '',
      icon: 'file',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'insurance_accepted',
      label: 'Insurance accepted',
      url: '',
      icon: 'check-circle',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'urgent_care_line',
      label: 'After-hours / urgent care line',
      url: '',
      icon: 'phone',
      classIcon: 'fill-red-600 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
  ],

  dentist_office: [
    {
      clientType: 'book_appointment',
      label: 'Book appointment',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'patient_portal',
      label: 'Patient portal',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'patient_forms',
      label: 'Patient forms / packet',
      url: '',
      icon: 'file',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'services_offered',
      label: 'Services offered',
      url: '',
      icon: 'settings',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'meet_team',
      label: 'Meet the team',
      url: '',
      icon: 'profile',
      classIcon: 'fill-violet-600 dark:fill-violet-400',
      classIconBg: 'bg-violet-100 dark:bg-violet-900/30',
    },
    {
      clientType: 'reviews_testimonials',
      label: 'Reviews / testimonials',
      url: '',
      icon: 'star-fill',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
  ],

  veterinarian: [
    {
      clientType: 'book_appointment',
      label: 'Book appointment',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'pet_portal',
      label: 'Patient (pet) portal',
      url: '',
      icon: 'lock',
      classIcon: 'fill-amber-500 dark:fill-amber-400',
      classIconBg: 'bg-amber-100 dark:bg-amber-900/30',
    },
    {
      clientType: 'refill_prescriptions',
      label: 'Refill prescriptions',
      url: '',
      icon: 'refresh',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'emergency_line',
      label: 'Emergency / after-hours line',
      url: '',
      icon: 'phone',
      classIcon: 'fill-red-600 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
    {
      clientType: 'new_client_forms',
      label: 'New client forms',
      url: '',
      icon: 'file',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
  ],

  school: [
    {
      clientType: 'enroll_apply',
      label: 'Enroll / apply',
      url: '',
      icon: 'file',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'parent_portal',
      label: 'Parent portal login',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'academic_calendar',
      label: 'Academic calendar',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'lunch_bus_routes',
      label: 'Lunch menu / bus routes',
      url: '',
      icon: 'clock',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'report_absence',
      label: 'Report an absence',
      url: '',
      icon: 'bell',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'donate',
      label: 'Give / donate',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-rose-500 dark:fill-rose-400',
      classIconBg: 'bg-rose-100 dark:bg-rose-900/30',
    },
  ],

  'pre-school': [
    {
      clientType: 'enroll_apply',
      label: 'Enroll / apply',
      url: '',
      icon: 'file',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'parent_portal',
      label: 'Parent portal login',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'academic_calendar',
      label: 'Academic calendar',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'lunch_bus_routes',
      label: 'Lunch menu / bus routes',
      url: '',
      icon: 'clock',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'report_absence',
      label: 'Report an absence',
      url: '',
      icon: 'bell',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'donate',
      label: 'Give / donate',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-rose-500 dark:fill-rose-400',
      classIconBg: 'bg-rose-100 dark:bg-rose-900/30',
    },
  ],

  k_8: [
    {
      clientType: 'enroll_apply',
      label: 'Enroll / apply',
      url: '',
      icon: 'file',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'parent_portal',
      label: 'Parent portal login',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'academic_calendar',
      label: 'Academic calendar',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'lunch_bus_routes',
      label: 'Lunch menu / bus routes',
      url: '',
      icon: 'clock',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'report_absence',
      label: 'Report an absence',
      url: '',
      icon: 'bell',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'donate',
      label: 'Give / donate',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-rose-500 dark:fill-rose-400',
      classIconBg: 'bg-rose-100 dark:bg-rose-900/30',
    },
  ],

  high_school: [
    {
      clientType: 'enroll_apply',
      label: 'Enroll / apply',
      url: '',
      icon: 'file',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'parent_portal',
      label: 'Parent portal login',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'academic_calendar',
      label: 'Academic calendar',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'lunch_bus_routes',
      label: 'Lunch menu / bus routes',
      url: '',
      icon: 'clock',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'report_absence',
      label: 'Report an absence',
      url: '',
      icon: 'bell',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'donate',
      label: 'Give / donate',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-rose-500 dark:fill-rose-400',
      classIconBg: 'bg-rose-100 dark:bg-rose-900/30',
    },
  ],

  college: [
    {
      clientType: 'apply_admissions',
      label: 'Apply / admissions',
      url: '',
      icon: 'file',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'visit_campus',
      label: 'Visit campus',
      url: '',
      icon: 'building',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'student_portal',
      label: 'Student portal',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'academic_calendar',
      label: 'Academic calendar',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'donate',
      label: 'Give to the college',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-rose-500 dark:fill-rose-400',
      classIconBg: 'bg-rose-100 dark:bg-rose-900/30',
    },
    {
      clientType: 'athletics_events',
      label: 'Athletics / events',
      url: '',
      icon: 'trophy',
      classIcon: 'fill-green-500 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
  ],

  university: [
    {
      clientType: 'apply_admissions',
      label: 'Apply / admissions',
      url: '',
      icon: 'file',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'visit_campus',
      label: 'Visit campus',
      url: '',
      icon: 'building',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'student_portal',
      label: 'Student portal',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'academic_calendar',
      label: 'Academic calendar',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'donate',
      label: 'Give to the college',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-rose-500 dark:fill-rose-400',
      classIconBg: 'bg-rose-100 dark:bg-rose-900/30',
    },
    {
      clientType: 'athletics_events',
      label: 'Athletics / events',
      url: '',
      icon: 'trophy',
      classIcon: 'fill-green-500 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
  ],

  salon: [
    {
      clientType: 'book_appointment',
      label: 'Book appointment',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'View services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reschedule',
      label: 'Cancel / reschedule',
      url: '',
      icon: 'refresh',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'gift_cards',
      label: 'Gift cards',
      url: '',
      icon: 'card',
      classIcon: 'fill-purple-500 dark:fill-purple-400',
      classIconBg: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      clientType: 'shop_products',
      label: 'Shop products',
      url: '',
      icon: 'link',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
  ],

  spa: [
    {
      clientType: 'book_appointment',
      label: 'Book appointment',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'View services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reschedule',
      label: 'Cancel / reschedule',
      url: '',
      icon: 'refresh',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'gift_cards',
      label: 'Gift cards',
      url: '',
      icon: 'card',
      classIcon: 'fill-purple-500 dark:fill-purple-400',
      classIconBg: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      clientType: 'shop_products',
      label: 'Shop products',
      url: '',
      icon: 'link',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
  ],

  wellness_service: [
    {
      clientType: 'book_appointment',
      label: 'Book appointment',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'View services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reschedule',
      label: 'Cancel / reschedule',
      url: '',
      icon: 'refresh',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'gift_cards',
      label: 'Gift cards',
      url: '',
      icon: 'card',
      classIcon: 'fill-purple-500 dark:fill-purple-400',
      classIconBg: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      clientType: 'shop_products',
      label: 'Shop products',
      url: '',
      icon: 'link',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
  ],

  charity: [
    {
      clientType: 'donate',
      label: 'Donate',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-rose-500 dark:fill-rose-400',
      classIconBg: 'bg-rose-100 dark:bg-rose-900/30',
    },
    {
      clientType: 'volunteer',
      label: 'Volunteer',
      url: '',
      icon: 'star',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'share',
      label: 'Share',
      url: '',
      icon: 'share',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'events_campaigns',
      label: 'Events / campaigns',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-purple-600 dark:fill-purple-400',
      classIconBg: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      clientType: 'newsletter_signup',
      label: 'Newsletter sign-up',
      url: '',
      icon: 'email',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'impact_report',
      label: 'Our impact / annual report',
      url: '',
      icon: 'document',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
  ],

  nonprofit: [
    {
      clientType: 'donate',
      label: 'Donate',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-rose-500 dark:fill-rose-400',
      classIconBg: 'bg-rose-100 dark:bg-rose-900/30',
    },
    {
      clientType: 'volunteer',
      label: 'Volunteer',
      url: '',
      icon: 'star',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'share',
      label: 'Share',
      url: '',
      icon: 'share',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'events_campaigns',
      label: 'Events / campaigns',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-purple-600 dark:fill-purple-400',
      classIconBg: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      clientType: 'newsletter_signup',
      label: 'Newsletter sign-up',
      url: '',
      icon: 'email',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'impact_report',
      label: 'Our impact / annual report',
      url: '',
      icon: 'document',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
  ],

  hvac: [
    {
      clientType: 'book_service',
      label: 'Book service',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'request_quote',
      label: 'Request a quote',
      url: '',
      icon: 'message',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'emergency_line',
      label: 'Emergency line',
      url: '',
      icon: 'phone',
      classIcon: 'fill-red-600 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'Services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reviews',
      label: 'Reviews',
      url: '',
      icon: 'star',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    {
      clientType: 'about',
      label: 'About page',
      url: '',
      icon: 'info-circle',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
  ],

  plumber: [
    {
      clientType: 'book_service',
      label: 'Book service',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'request_quote',
      label: 'Request a quote',
      url: '',
      icon: 'message',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'emergency_line',
      label: 'Emergency line',
      url: '',
      icon: 'phone',
      classIcon: 'fill-red-600 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'Services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reviews',
      label: 'Reviews',
      url: '',
      icon: 'star',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    {
      clientType: 'about',
      label: 'About page',
      url: '',
      icon: 'info-circle',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
  ],

  home_service: [
    {
      clientType: 'book_service',
      label: 'Book service',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'request_quote',
      label: 'Request a quote',
      url: '',
      icon: 'message',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'emergency_line',
      label: 'Emergency line',
      url: '',
      icon: 'phone',
      classIcon: 'fill-red-600 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'Services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reviews',
      label: 'Reviews',
      url: '',
      icon: 'star',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    {
      clientType: 'about',
      label: 'About page',
      url: '',
      icon: 'info-circle',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
  ],

  house_cleaner: [
    {
      clientType: 'book_cleaning',
      label: 'Book a cleaning',
      url: '',
      icon: 'home',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'reschedule',
      label: 'Reschedule',
      url: '',
      icon: 'refresh',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'Services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reviews',
      label: 'Reviews',
      url: '',
      icon: 'star',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    {
      clientType: 'get_quote',
      label: 'Get a quote',
      url: '',
      icon: 'message',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
  ],

  auto_maintenance: [
    {
      clientType: 'book_service',
      label: 'Book service',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'get_quote',
      label: 'Get a quote',
      url: '',
      icon: 'message',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'services_offered',
      label: 'Services offered',
      url: '',
      icon: 'settings',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'reviews',
      label: 'Reviews',
      url: '',
      icon: 'star',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    {
      clientType: 'about',
      label: 'About',
      url: '',
      icon: 'info-circle',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'roadside_tow_contact',
      label: 'Roadside / tow contact',
      url: '',
      icon: 'phone',
      classIcon: 'fill-red-500 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
  ],

  dog_walker: [
    {
      clientType: 'book_walk_sitting',
      label: 'Book a walk / sitting',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-amber-500 dark:fill-amber-400',
      classIconBg: 'bg-amber-100 dark:bg-amber-900/30',
    },
    {
      clientType: 'client_portal',
      label: 'Client portal',
      url: '',
      icon: 'lock',
      classIcon: 'fill-indigo-600 dark:fill-indigo-400',
      classIconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      clientType: 'about',
      label: 'About',
      url: '',
      icon: 'info-circle',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'leave_review',
      label: 'Leave a review',
      url: '',
      icon: 'star-fill',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    {
      clientType: 'meet_greet',
      label: 'Book a meet & greet',
      url: '',
      icon: 'profile',
      classIcon: 'fill-violet-600 dark:fill-violet-400',
      classIconBg: 'bg-violet-100 dark:bg-violet-900/30',
    },
  ],

  pet_groomer: [
    {
      clientType: 'book_grooming_boarding',
      label: 'Book grooming / boarding',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'Services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'vaccine_requirements',
      label: 'Vaccine requirements',
      url: '',
      icon: 'file',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reviews',
      label: 'Reviews',
      url: '',
      icon: 'star',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
  ],

  pet_boarding: [
    {
      clientType: 'book_grooming_boarding',
      label: 'Book grooming / boarding',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
    {
      clientType: 'services_pricing',
      label: 'Services & pricing',
      url: '',
      icon: 'dollar',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'vaccine_requirements',
      label: 'Vaccine requirements',
      url: '',
      icon: 'file',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'reviews',
      label: 'Reviews',
      url: '',
      icon: 'star',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
  ],

  other: [
    {
      clientType: 'make_payment',
      label: 'Make a payment',
      url: '',
      icon: 'card',
      classIcon: 'fill-green-600 dark:fill-green-400',
      classIconBg: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      clientType: 'leave_review',
      label: 'Leave a review',
      url: '',
      icon: 'star-fill',
      classIcon: 'fill-yellow-500 dark:fill-yellow-400',
      classIconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    {
      clientType: 'book_appointment',
      label: 'Schedule an appointment',
      url: '',
      icon: 'calendar',
      classIcon: 'fill-blue-600 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'hours_directions',
      label: 'Hours and directions',
      url: '',
      icon: 'marker',
      classIcon: 'fill-red-500 dark:fill-red-400',
      classIconBg: 'bg-red-100 dark:bg-red-900/30',
    },
    {
      clientType: 'about_us',
      label: 'About us',
      url: '',
      icon: 'info-circle',
      classIcon: 'fill-slate-600 dark:fill-slate-400',
      classIconBg: 'bg-slate-100 dark:bg-slate-800/30',
    },
    {
      clientType: 'order_now',
      label: 'Order now',
      url: '',
      icon: 'box',
      classIcon: 'fill-orange-500 dark:fill-orange-400',
      classIconBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      clientType: 'files',
      label: 'Files',
      url: '',
      icon: 'file',
      classIcon: 'fill-blue-500 dark:fill-blue-400',
      classIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      clientType: 'open_app',
      label: 'Open / Get App',
      url: '',
      icon: 'phone',
      classIcon: 'fill-teal-600 dark:fill-teal-400',
      classIconBg: 'bg-teal-100 dark:bg-teal-900/30',
    },
  ],
} satisfies Record<IndustryT, SuggestedActionLink[]>

export const CUSTOM_LINK_TEMPLATE: SuggestedActionLink = {
  clientType: 'custom',
  label: '',
  url: '',
  icon: 'link',
  classIcon: 'fill-violet-600 dark:fill-violet-400',
  classIconBg: 'bg-violet-100 dark:bg-violet-900/30',
}
